import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Paper, Stack, Typography, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {AgGridReact} from "ag-grid-react";
import {
    ColDef,
    ColumnMovedEvent,
    ColumnVisibleEvent,
    GetRowIdParams,
    GridReadyEvent,
    IServerSideDatasource,
    SelectionChangedEvent,
    SideBarDef
} from "ag-grid-community";
import {useMutation} from "@tanstack/react-query";
import {AxiosError} from "axios";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import CustomLoader from "../../../../util/component/CustomLoader";
import {expiredActionPropsState, filterParams, practicesListSummaryState, selectedPracticesListState} from "../../state";
import {
    EXPIRED_DEFAULT_COL_DEF,
    EXPIRED_PRACTICES_QUERY_KEY,
    EXPIRED_SIDE_BAR,
    ExpiredInsertionAction,
    getExpiredPracticesListColmnDef,
    GRID_LIST_PAGE_SIZE
} from "../../config";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {deepClone} from "../../../../util/state/commonFunctions";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {fetchPracticesList, fetchPracticesListSummary} from "../../services/http.services";
import { PracticesListSummaryReturn } from "../../models/models";
import { AgGridCustomToolbarProps } from "../../../../util/component/AgGridCustomToolbar";
import ToolbarInfo from "../expired-practices-list-filter/toolbar-info/ToolbarInfo";
import ToolbarActions from "../expired-practices-list-filter/toolbar-action/ToolbarAction";
import { setSnackbar } from "../../../../util/state/globalAction";
import OperatorNoteButton from "../add-new-note/OperatorNoteButton";
import PivotButton from "../pivot/PivotButton";
import NewPracticeButton from "../create-new-practice/NewPracticeButton";
import ActionMenuButton from "../../action-insertion/menu-button/ActionMenuButton";
import ActionInsertion from "../../action-insertion/ActionInsertion";
import { Close } from "@mui/icons-material";
import RecoveryEntrustment from "../../recovery-entrustment/RecoveryEntrustment";
import ClosePracticeButton from "../close-practice/ClosePracticeButton";
import { generateExpiredActionProps } from "../../utils";

const ExpiredPracticesList = () => {
    const theme = useTheme();
    const {t} = useTranslation(['expiredManagement']);
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);

    const [filterP, setFilterP] = useRecoilState(filterParams);

    const setSummary = useSetRecoilState(practicesListSummaryState);
    const [selectedPractices, setSelectedPractices] = useRecoilState(selectedPracticesListState);
    const setExpiredActionProps = useSetRecoilState(expiredActionPropsState);
    
    const [openRecoveryEntrustmentDialog, setOpenRecoveryEntrustmentDialog] = useState(false);
    const [actionDialogProps, setActionDialogProps] = useState<{
        title: string,
        open: boolean
    }>({
        title: '',
        open: false
    });

    const gridRef = useRef<AgGridReact>(null);

    const viewErrorMessage = (message: string, type = snackbarTypes.error) => {
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t(message),
                    type: type,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const columnDefs = useMemo<ColDef[]>(() => {
        return getExpiredPracticesListColmnDef(t);
    }, [t]);

    const defaultColDef = useMemo<ColDef>(() => EXPIRED_DEFAULT_COL_DEF, []);

    const columnStateStorageKey = useMemo(() => {
        return `__EXPIRED_MANAGEMENT_PENDING.tableColumsConfig:${globalData.user.username}`;
    }, [globalData.user.username]);

    const sideBar = useMemo<SideBarDef>(() => EXPIRED_SIDE_BAR, []);

    const toolbar = useMemo<AgGridCustomToolbarProps>(() => ({
        renderInfo: ToolbarInfo,
        renderActions: ToolbarActions
    }), []);

    const onColumnVisible = useCallback((event: ColumnVisibleEvent) => {
        localStorage.setItem(columnStateStorageKey, JSON.stringify(event.columnApi.getColumnState()));
    }, [columnStateStorageKey]);

    const onColumnMoved = useCallback((event: ColumnMovedEvent) => {
        localStorage.setItem(columnStateStorageKey, JSON.stringify(event.columnApi.getColumnState()));
    }, [columnStateStorageKey]);

    const onGridReady = useCallback((event: GridReadyEvent) => {
        const colState = JSON.parse(localStorage.getItem(columnStateStorageKey) || '[]');
        event.columnApi.applyColumnState({
            state: colState,
            applyOrder: true
        });

        event.api.sizeColumnsToFit();
    }, [columnStateStorageKey]);

    const onSelectedChanged = (event: SelectionChangedEvent) => {

        setSelectedPractices(event.api.getSelectedRows()/*.map((p: any) => p.expiredCode)*/);

    }

    const onGetRowId = useCallback((params: GetRowIdParams) => {
        return params.data["expiredCode"];
    }, []);

    const fetchPracticesListMutation = useMutation([EXPIRED_PRACTICES_QUERY_KEY.GET_EXPIRED_PRACTICE_SUMMARY], fetchPracticesList);
    const fetchPracticesListSummaryMutation = useMutation(fetchPracticesListSummary);

    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {

            if (filterP === null) {

                params.success({rowCount: 0, rowData: [] });
                params.api.showNoRowsOverlay();

                return;
            }

            let filterParamsClone = deepClone(filterP);

            const sortBy =
                params.request.sortModel[0] ?
                    {
                        field: params.request.sortModel[0].colId,
                        order: params.request.sortModel[0].sort
                    } :
                    undefined;

            const limit =
                (params.request.endRow !== undefined && params.request.startRow !== undefined) ?
                    (params.request.endRow - params.request.startRow) :
                    undefined;

            if (!filterParamsClone) {
                filterParamsClone = {};
            }

            filterParamsClone = {
                filters: filterP,
                limit: limit,
                start: params.request.startRow              
            }

            if (!!sortBy) {
                filterParamsClone.sortBy = sortBy;
            }

            if (!!filterParamsClone.filters.tenants) {
                const ind = filterParamsClone.filters.tenants.findIndex((elem: string) => elem === 'all');
                if (ind > -1) {
                    filterParamsClone.filters.tenants =
                        globalData.user.legalEntities
                            .filter((elem: any) => elem !== 'all');
                } else {
                    filterParamsClone.filters = {
                        ...filterParamsClone.filters,
                        tenants: filterParamsClone.filters.tenants
                    }
                }
            }

            // if (!!filterParamsClone.filters.dealers) {
            //     const ind = filterParamsClone.filters.dealers.findIndex((elem: string) => elem === 'all');
            //     if (ind > -1) {
            //         filterParamsClone.filters.dealers =
            //             globalData.user.legalEntities
            //                 .filter((elem: any) => elem.name !== 'all');
            //     } else {
            //         filterParamsClone.filters = {
            //             ...filterParamsClone.filters,
            //             dealers: filterParamsClone.filters.dealers
            //         }
            //     }
            // }

            setSelectedPractices([]);
            params.api.deselectAll();

            let totalSummary: number;
            fetchPracticesListSummaryMutation.mutateAsync({
                mobilityStores: globalData?.user?.mobilityStores?.map(((elem:any) => elem.sapId)),
                auth: globalData?.user?.scopes,
                filters: filterParamsClone.filters
            }).then((response: any) => {

                const data: PracticesListSummaryReturn = response?.data;
                
                setSummary(data.summary);
                totalSummary = data.summary.total;

                fetchPracticesListMutation.mutateAsync({
                    mobilityStores: globalData?.user?.mobilityStores?.map(((elem:any) => elem.sapId)),
                    auth: globalData?.user?.scopes,
                    filters: filterParamsClone.filters,
                    limit: filterParamsClone.limit,
                    start: filterParamsClone.start,
                    sortBy: filterParamsClone?.sortBy
                }).then((response: any) => {
                        params.success({
                           
                            rowData: response.data.rows,
                            rowCount: totalSummary ?? 0
                        });

                        if (totalSummary === 0) {
                            params.api.showNoRowsOverlay();
                        }
                    })
                    .catch((err: AxiosError) => {
                        setSummary(undefined);
                        params.fail();
                        params.api.hideOverlay();
                        viewErrorMessage(t('translation:error.tryLater'));
                    });

            }).catch(() => {
                setSummary(undefined);
                params.fail();
                params.api.hideOverlay();
            });
        }
    }), [filterP])

    useEffect(() => {
        if (gridRef?.current) {
            gridRef.current.api.refreshServerSide();
        }
    }, [serverSideDatasource])

    // const loadingCellRenderer = useCallback(CustomLoadingCellRendererProps, []);

    const handleInsertAction = async (actionType: any) => {
        if([
                ExpiredInsertionAction.SMS1, 
                ExpiredInsertionAction.SMS2,
                ExpiredInsertionAction.SMS3,
                ExpiredInsertionAction.NEW_SMS,
                ExpiredInsertionAction.RECORD_CALL,
                ExpiredInsertionAction.IN_CAR_REPAIR_SHOP,
                ExpiredInsertionAction.IN_DMS,
                ExpiredInsertionAction.NO_VEHICLE,
                ExpiredInsertionAction.POSITIVE_RECOVERY,
                ExpiredInsertionAction.NEGATIVE_RECOVERY,
                ExpiredInsertionAction.MISAPPROPRIATION,
                ExpiredInsertionAction.THEFT,
                ExpiredInsertionAction.RECOVERY
            ].includes(actionType)) 
        {

            await setExpiredActionProps(generateExpiredActionProps(actionType, selectedPractices));
    
            if(actionType == ExpiredInsertionAction.RECOVERY)
                setOpenRecoveryEntrustmentDialog(true);
            else
                setActionDialogProps({
                    title: actionType,
                    open: true
                });
        }
    };

    const handleActionDialogClose = () => {

        setActionDialogProps({
            ...actionDialogProps,
            open: false
        });
        setExpiredActionProps(null);
    }

    const handleRecoveryEntrustmentDialogClose = () => {
        
        setOpenRecoveryEntrustmentDialog(false);
        setExpiredActionProps(null);
    }

    return (
        <>
            <AgGridCustomTable
                gridRef={gridRef}
                columnDefs={columnDefs}
                gridOptions={
                    {
                        defaultColDef: defaultColDef,
                        rowSelection: "multiple",
                        rowMultiSelectWithClick: true,
                        suppressRowClickSelection: true
                    }
                }
                onColumnVisible={onColumnVisible}
                onColumnMoved={onColumnMoved}
                onGridReady={onGridReady}
                serverSideDatasource={serverSideDatasource}
                rowModelType="serverSide"
                serverSideInfiniteScroll
                paginationPageSize={GRID_LIST_PAGE_SIZE}
                cacheBlockSize={GRID_LIST_PAGE_SIZE}
                sideBar={sideBar}
                toolbar={toolbar}
                suppressDragLeaveHidesColumns
                suppressMultiSort
                enableCellTextSelection
                onSelectionChanged={onSelectedChanged}
                getRowId={onGetRowId}

                // loadingCellRenderer={loadingCellRenderer}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{display: "flex", justifyContent: "flex-end", my: theme.spacing(2)}}>
                <NewPracticeButton />

                <ClosePracticeButton />

                <OperatorNoteButton />

                <ActionMenuButton
                    forView
                    disabled={!selectedPractices || selectedPractices.length <= 0}
                    handleInsertAction={handleInsertAction}
                />

                <PivotButton />
            </Stack>

            <CustomLoader loading={fetchPracticesListMutation.isLoading || fetchPracticesListSummaryMutation.isLoading}/>

            <Dialog open={actionDialogProps.open} fullWidth maxWidth={"xl"}>
                <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    <Typography>{t(`common.actions.dialog.title.${actionDialogProps.title}`)}</Typography>

                    <IconButton 
                        onClick={handleActionDialogClose} 
                        sx={{ml: theme.spacing(4), mr: "0px"}}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <ActionInsertion
                        //forView
                        onConfirm={() => {
                            handleActionDialogClose();
                            setFilterP((prev:any) => {
                                return {...prev};
                            })
                        }}
                        onClose={handleActionDialogClose}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={openRecoveryEntrustmentDialog} fullWidth maxWidth={"xl"} >
                <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    <Typography>{t('common.actions.dialog.title.RECUPERATORE')}</Typography>

                    <IconButton 
                        onClick={handleRecoveryEntrustmentDialogClose} 
                        sx={{ml: theme.spacing(4), mr: "0px"}}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <RecoveryEntrustment
                        onConfirm={() => {
                            handleRecoveryEntrustmentDialogClose();
                            setFilterP((prev:any) => {
                                return {...prev};
                            })
                        }}
                        onClose={handleRecoveryEntrustmentDialogClose}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}

export default ExpiredPracticesList;
